// @flow
import URI from 'urijs';
import domainsService from '@clubmed/domains-service';
import { appQueryParameters, oidc as oidcUtils } from '@clubmed/utils';

import config from 'Config';
import { QUICK_SEARCH_URL } from 'Routes/constants';
import { isProduction } from 'Utils/context/contextUtils';
import { ACCESS_TOKEN, USER_INFO, SELLER_LOGGED, SELLER_INFO } from 'Utils/ple-utils/cookies';
import { decryptHeptaSeller } from 'Utils/seller/sellerUtils';

type sellerInfoType = {
  agent?: string,
  agency?: string,
  email: string,
};
type userInfoType = {
  customerType?: string,
  isLoggedIn?: boolean,
  isQuickLoggedIn?: boolean,
  neolaneId?: string,
};

export function initAppContext(
  uri: string,
  { cookies = {}, locale = 'fr-FR', originalUrl, requestId, userAgent }: $Shape<AppContext> = {},
): AppContext {
  const [language, country] = domainsService.isValidLocale(locale) ? locale.split('-') : [];
  const encodedUserInfo: ?userInfoType = cookies[USER_INFO]
    ? oidcUtils.fromUriEncodedState(cookies[USER_INFO])
    : undefined;
  const sellerInfo: ?sellerInfoType = cookies[SELLER_INFO]
    ? oidcUtils.fromUriEncodedState(cookies[SELLER_INFO])
    : undefined;
  const uriObject = new URI(uri);
  const queryParameters = uriObject.query(true);
  const domainServiceForHostname = domainsService.forHostname(uriObject.hostname());
  const availableLocales = domainServiceForHostname.getAllLocales();

  const {
    cmta: { cm_ta },
    cxo: { cxo_sales_network_id },
    indirect,
  } = appQueryParameters;

  const isCmta = queryParameters[cm_ta] !== undefined;
  const isIndirect = queryParameters[indirect] !== undefined;
  const isDirectCxo = !!queryParameters[cxo_sales_network_id] && !isIndirect && !isCmta;
  const isIndirectCxo = !!queryParameters[cxo_sales_network_id] && isIndirect && !isCmta;

  return {
    authenticationToken: cookies[ACCESS_TOKEN] || cookies.CM_SELLER_TOKEN || '',
    availableLocales,
    bypassCache: queryParameters[appQueryParameters.bypassCache] === 'true',
    canUseOnlinePayment: domainsService.getEnabledPaymentProviders(locale).length > 0,
    cmsContent: queryParameters.commonContent || config.get('cms.commonContent'),
    cmsRelease: queryParameters.commonRelease || config.get('cms.commonRelease'),
    cmta: isCmta
      ? {
          agency: sellerInfo?.agency || '',
          agent: sellerInfo?.agent || '',
          email: sellerInfo?.email || '',
          searchType: uriObject.path() === QUICK_SEARCH_URL ? 'quick' : 'advanced',
        }
      : {},
    cookies,
    country: country || '',
    customerNeolaneId: queryParameters.cxo_neolid,
    customerType: encodedUserInfo?.customerType || '',
    cxoQueryParams: {
      cxo_azur_code: queryParameters.cxo_azur_code,
      cxo_sales_channel: queryParameters.cxo_sales_channel,
      cxo_country_code: queryParameters.cxo_country_code,
      cxo_user_id:
        typeof queryParameters.cxo_id === 'string'
          ? decryptHeptaSeller(queryParameters.cxo_id)
          : '',
    },
    displayUnicodeFields: domainsService.shouldDisplayUnicodeFields(locale) || false,
    // TODO replace this by - and improve for SSR - GoogleAnalytics tracking helper
    googleTrackingId: queryParameters.visitorGUID || cookies._ga || null,
    headers: {},
    isProduction: isProduction(),
    isCmta,
    isCxo: isDirectCxo || isIndirectCxo,
    isDirectCxo,
    isIndirectCxo,
    isSeller: isCmta || isDirectCxo || isIndirectCxo,
    isSellerLogged: cookies[SELLER_LOGGED] === 'true',
    isUserLogged: encodedUserInfo?.isLoggedIn || false,
    isUserQuickLogged: encodedUserInfo?.isQuickLoggedIn || false,
    language: language || '',
    lines: [],
    locale,
    neolaneId: cookies.cm_client_uid || (encodedUserInfo ? encodedUserInfo.neolaneId : null),
    originalUrl,
    pagesContent: queryParameters.pagesContent || config.get('cms.pagesContent'),
    pagesRelease: queryParameters.pagesRelease || config.get('cms.pagesRelease'),
    requestId,
    userAgent: userAgent || '',
  };
}
